import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Notice } from '@/components/Modal';
import { SignaturePadModal } from '@/pages/approval/SignaturePadModal';
import { useMySignature } from '@/pages/approval/useApproval';
import {
  PlanDateList,
  PlanMonthChart,
  PlanProgressStrip,
  measurePlans,
  todayIso,
  type PlanDay,
} from '@/pages/hr/leaveParts';
import { AttendancePanel } from '@/pages/mypage/AttendancePanel';
import { LeavePlanEditor } from '@/pages/mypage/LeavePlanEditor';
import { SubstituteLeavePanel } from '@/pages/mypage/SubstituteLeavePanel';
import {
  useApplyLeavePlan,
  useLeaveCalendar,
  useMyLeave,
  useMyLeaveConsents,
  useSignLeaveConsent,
  useUpcomingLeavePlans,
  type LeaveCalendar,
  type LeavePeriod,
  type LeavePlanApplyResult,
  type LeaveFiscal,
  type LeavePromotion,
  type LeaveUsage,
  type MyLeave,
  type UpcomingLeavePlan,
} from '@/pages/mypage/useLeave';

/**
 * 연차관리 탭 (DOC-47 §3.2).
 *
 * 예전에는 「총 연차 · 사용 · 잔여」 세 칸과 사용 내역이 전부였다. 그 화면이 답하지 못한 것이 셋이다 —
 * **언제 사라지는가 · 얼마를 더 써야 하는가 · 얼마가 수당으로 남는가.** 셋 다 잔여 숫자만으로는
 * 알 수 없고, 그래서 매년 12월에 인사팀에 전화가 몰렸다.
 *
 * ## 금액은 적지 않는다
 * 수당은 **일수까지**만 말한다. 통상임금이 시스템 어디에도 없어(급여명세서는 실지급 합계 한 칸뿐이다)
 * 여기서 곱하면 그것은 추정이고, 화면에 뜬 추정 금액은 곧 약속으로 읽힌다.
 *
 * ## 「확정 전 잠정값」을 숨기지 않는다
 * 의무사용 일수는 인사팀이 지침 최신본으로 확정하기 전까지 잠정값이다. 그 딱지를 붙이는 것이
 * 값을 감추는 것보다 낫다 — 감추면 화면이 아무것도 말하지 않고, 확정은 그만큼 미뤄진다.
 */
/**
 * 연차관리 — <b>서브탭 셋</b> (사용자 지시 2026-09-22).
 *
 * <pre>
 *   현황     연차 부여·사용·소멸·촉진·휴무 계획 (예전의 이 화면 전부)
 *   대체휴무 공휴일에 일한 날을 모아 신청으로 보낸다 (V326 의 출퇴근 기록이 근거)
 *   출퇴근   내 기록·웹에서 찍기·정정 요청 (찍는 것은 앱이 먼저다)
 * </pre>
 *
 * <p>셋을 한 화면에 이어 붙이지 않은 이유는 <b>여는 이유가 다르기</b> 때문이다 — 연차 잔여를
 * 보러 온 사람과 「지난주 퇴근이 안 찍혔다」 로 온 사람은 같은 줄을 찾지 않는다.
 */
export function LeaveTab() {
  const [subParams] = useSearchParams();
  // 주소로 칸을 바로 연다(`?tab=leave&sub=substitute`) — 릴리즈 노트·안내 링크가 그 칸으로 도착한다.
  const [sub, setSub] = useState<'status' | 'substitute' | 'attendance'>(() => {
    const wanted = subParams.get('sub');
    return wanted === 'substitute' || wanted === 'attendance' ? wanted : 'status';
  });

  // 연차관리 탭에 이미 들어와 있는 사람에게도 링크(사이드바 검색 등)가 듣게 한다.
  useEffect(() => {
    const wanted = subParams.get('sub');
    if (wanted === 'status' || wanted === 'substitute' || wanted === 'attendance') setSub(wanted);
  }, [subParams]);

  return (
    <div className="space-y-4">
      <div className="flex gap-1 flex-wrap" role="tablist">
        {LEAVE_SUB_TABS.map((item) => (
          <button
            key={item.key}
            role="tab"
            aria-selected={sub === item.key}
            onClick={() => setSub(item.key)}
            className="rounded-lg"
            style={{
              fontSize: '13px',
              padding: '5px 12px',
              fontWeight: sub === item.key ? 700 : 500,
              background: sub === item.key ? 'var(--accent-bg)' : 'transparent',
              color: sub === item.key ? 'var(--accent)' : 'var(--text-secondary)',
              border: `1px solid ${sub === item.key ? 'var(--accent-light)' : 'var(--border)'}`,
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {sub === 'status' && <LeaveStatus />}
      {sub === 'substitute' && <SubstituteLeavePanel />}
      {sub === 'attendance' && <AttendancePanel />}
    </div>
  );
}

const LEAVE_SUB_TABS = [
  { key: 'status', label: '현황' },
  { key: 'substitute', label: '시간외근무' },
  { key: 'attendance', label: '출퇴근' },
] as const;

/** 예전의 연차관리 화면 그대로 — 부여·사용·소멸·촉진·휴무 계획. */
function LeaveStatus() {
  const navigate = useNavigate();
  const { data, isPending, error } = useMyLeave();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const calendar = useLeaveCalendar(year);
  /**
   * 휴무 계획을 달력에서 고치는 중 (사용자 지시 2026-08-26).
   *
   * 계획서는 더 이상 결재 서식이 아니다 — 여기서 날짜를 골라 바로 저장한다. 촉진 안내의 회신도
   * 같은 모달로 보낸다: 1차 촉구가 요구하는 것이 「시기를 알려 달라」 이고, 그 답이 곧 이 달력이다.
   */
  const [params] = useSearchParams();
  // 주소로 곧바로 달력을 연다(`/mypage?tab=leave&plan=1`) — 대화(AI)가 「휴무 계획 세우고 싶어」 에
  // 내미는 링크다. 안내만 하면 탭을 찾고 버튼을 찾는 두 걸음이 더 남는다.
  const [editing, setEditing] = useState(() => params.get('plan') === '1');

  if (isPending) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }
  if (error) return <Notice tone="error">연차 자료를 불러오지 못했습니다.</Notice>;

  const leave: MyLeave | undefined = data;
  const current = leave?.current ?? null;

  /**
   * 그 해의 계획. <b>이행 여부는 서버가 판정해 보낸다</b>({@code fulfilled}).
   *
   * <p>한때 화면이 계획 날짜와 사용 날짜의 교집합으로 셌다. 주말 계획을 허용하면서 그 셈이 틀렸다 —
   * 사용 목록은 <b>일수 규칙</b>으로 펼쳐져 오므로 토요일이 없고, 그러면 토요일 계획이 영원히
   * 미이행으로 남는다. 판정은 기간을 통째로 보는 서버 한 곳에만 둔다.
   */
  const planDays: PlanDay[] = (calendar.data?.plans ?? []).map((plan) => ({
    date: plan.date,
    days: plan.days,
    fulfilled: plan.fulfilled,
  }));
  const progress = measurePlans(planDays, todayIso());

  return (
    <div className="space-y-4">
      <ConsentNotice />
      <PromotionNotice promotions={leave?.promotions ?? []} onRespond={() => setEditing(true)} />

      {/* 계획은 세워 뒀는데 신청을 안 한 날 — 이 화면에서 유일하게 「지금 할 일」 이라 맨 위다. */}
      <UpcomingPlans highlight={params.get('apply') === '1'} />

      {!current && leave?.fiscal?.total == null ? (
        <Notice tone="info">
          등록된 연차 부여가 없습니다. 입사일이 인사 정보에 등록되면 다음 날부터 자동으로 부여됩니다.
        </Notice>
      ) : (
        <Summary period={current} fiscal={leave?.fiscal ?? null} standard={leave!.standard} />
      )}

      {/*
        내 계획 — <b>휴무 관리와 같은 그림</b>이다(진척 줄 · 달별 막대 · 날짜 목록). 인사팀이 보는
        표와 내가 보는 화면이 다른 모양이면, 「팀장이 보는 내 진척」 과 「내가 보는 내 진척」 을 따로
        배워야 한다(사용자 지시 2026-08-26).

        <b>할 일 버튼 둘도 이 카드로 들어왔다</b>(사용자 지시 2026-08-26). 요약 아래에 맨몸으로
        떠 있던 버튼 줄은 어느 상자에도 속하지 않아 눈이 매번 다시 찾았다. 신청과 계획은 둘 다
        「이 해의 휴무를 어떻게 할 것인가」 라 계획 카드가 제자리다.
      */}
      <div className="card" style={{ padding: '12px 14px' }}>
        <div className="flex items-center gap-3 flex-wrap">
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
            내 휴무 계획 · {year}년
          </h3>
          {calendar.isPending ? (
            <span style={mutedText}>불러오는 중…</span>
          ) : planDays.length === 0 ? (
            <span style={mutedText}>이 해에 세운 계획이 없습니다.</span>
          ) : (
            <PlanProgressStrip progress={progress} />
          )}
          <span className="ml-auto flex items-center gap-2">
            {current && (
              <button className="btn-primary" onClick={() => navigate('/approval/draft?formKey=LEAVE')}>
                연차 신청
              </button>
            )}
            {/* 계획은 결재가 아니다 — 달력에서 골라 바로 저장한다(잔여는 그대로다). */}
            <button className="btn-secondary" onClick={() => setEditing(true)}>
              {planDays.length === 0 ? '휴무 계획 세우기' : '휴무 계획 고치기'}
            </button>
          </span>
        </div>
        {/*
          결재를 거치지 않은 연차는 원장에 오지 않는다. 그 사실을 화면이 먼저 말한다 —
          예전에는 구두로 쉬고 나중에 인사팀이 손으로 넣어 잔여가 늦게 줄었다.
        */}
        {current && (
          <p style={{ ...mutedText, marginTop: '6px' }}>승인된 결재만 잔여에서 빠집니다.</p>
        )}
      </div>

      {/*
        달별 막대와 연 캘린더를 <b>한 줄에</b> 세운다(사용자 지시 2026-08-26). 둘은 같은 자료를
        다른 눈금으로 보는 그림이라(달 합계 / 날짜 하나), 위아래로 쌓으면 화면을 굴려야 비교가 된다.
        좁은 화면에서는 한 칸씩 내려간다 — 31칸 격자가 반쪽 폭에서는 읽히지 않는다.

        두 카드는 <b>키를 맞춘다</b>(items-stretch, 사용자 지시 2026-08-26) — 나란히 선 카드의
        아랫변이 어긋나면 한쪽이 잘린 것처럼 보인다. 막대 그림이 남는 높이를 먹고, 격자는
        위로 붙는다.
      */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
        <PlanMonthChart year={year} plans={planDays} />
        <div className="card h-full" style={{ padding: '12px 14px' }}>
          <YearCalendar
            year={year}
            onYear={setYear}
            data={calendar.data}
            loading={calendar.isPending}
          />
        </div>
      </div>

      {planDays.length > 0 && <PlanList plans={planDays} />}

      <UsageList usages={leave?.usages ?? []} />

      {(leave?.periods.length ?? 0) > 1 && <PeriodHistory periods={leave!.periods} />}

      {editing && <LeavePlanEditor year={year} onClose={() => setEditing(false)} />}
    </div>
  );
}

// ── 다가온 계획 ──────────────────────────────────────────────────────────────

/**
 * 곧 다가오는데 <b>아직 연차 신청을 올리지 않은</b> 계획 (사용자 지시 2026-08-26).
 *
 * <h2>왜 이 배너가 필요한가</h2>
 * 계획은 결재가 아니라 그냥 저장된다(DOC-47 §15). 가벼워진 대신 <b>신청과 끊겼다</b> — 달력에
 * 찍어 두고 정작 결재를 안 올리면, 그날은 무단결근이 되거나 인사팀이 나중에 손으로 원장에 넣는다.
 * 홈 할일도 같은 줄을 띄우고 여기로 데려온다.
 *
 * <h2>누르면 상신까지 간다</h2>
 * 기안 화면으로 보내지 않는다 — 계획에 이미 날짜·일수가 다 있고, 결재선은 규정이 세운다. 사람이
 * 다시 채울 것이 없는 화면을 한 번 더 거치게 하면 그 자리에서 또 미룬다. <b>다만 잔여는 지금
 * 줄지 않는다</b> — 상신이지 승인이 아니다.
 *
 * <h2>올린 것은 「진행 중」 으로 남는다 (사용자 지시 2026-08-26)</h2>
 * 상신한 순간 그 계획은 할 일에서 빠지지만 <b>화면에서 사라지지는 않는다</b>. 사라지면 「내가 올렸던가」
 * 를 결재함에 가서 확인해야 하고, 확인하러 간 김에 하나 더 올리는 일이 실제로 있었다.
 */
function UpcomingPlans({ highlight }: { highlight: boolean }) {
  const navigate = useNavigate();
  const upcoming = useUpcomingLeavePlans();
  const apply = useApplyLeavePlan();
  const [applied, setApplied] = useState<LeavePlanApplyResult | null>(null);

  if (applied) {
    return (
      <Notice tone="success">
        <b>연차 신청을 올렸습니다</b> — {period(applied.startDate, applied.endDate)} · {days(applied.days)}일.
        결재가 완료되면 잔여에서 빠집니다.
        <button
          className="btn-secondary"
          style={{ marginLeft: '8px', fontSize: '12px', padding: '3px 10px' }}
          onClick={() => navigate(`/approval?box=submitted&id=${applied.documentId}`)}
        >
          결재 진행 보기
        </button>
      </Notice>
    );
  }

  const all = upcoming.data ?? [];
  const plans = all.filter((plan) => plan.status === 'PENDING');
  const submitted = all.filter((plan) => plan.status === 'IN_PROGRESS');

  if (upcoming.isPending || all.length === 0) return null;

  // 올릴 것이 없으면 진행 중 줄만 남는다 — 「할 일 없음」 을 빈 화면으로 말하지 않는다.
  if (plans.length === 0) return <SubmittedPlans plans={submitted} />;

  const first = plans[0]!;

  return (
    <>
    <Notice tone={highlight || first.dDay <= 2 ? 'warning' : 'info'}>
      <b>{dDayLabel(first.dDay)} 휴무 계획</b> — {period(first.startDate, first.endDate)} ·{' '}
      {days(first.days)}일{plans.length > 1 && ` (그 밖에 ${plans.length - 1}건)`}
      <span style={{ display: 'block', fontSize: '12px', marginTop: '2px' }}>
        아직 연차 신청을 올리지 않았습니다. 지금 올려 드릴까요? 결재선은 규정대로 자동으로 섭니다.
      </span>
      <span className="flex items-center gap-2" style={{ marginTop: '8px' }}>
        <button
          className="btn-primary"
          disabled={apply.isPending}
          onClick={() =>
            apply.mutate(
              { startDate: first.startDate, endDate: first.endDate, days: first.days },
              { onSuccess: setApplied },
            )
          }
        >
          {apply.isPending ? '올리는 중…' : '연차 신청 자동으로 올리기'}
        </button>
        {/* 직접 쓰고 싶은 사람도 있다 — 사유를 적거나 결재선을 바꾸려는 경우다. */}
        <button
          className="btn-secondary"
          onClick={() =>
            navigate('/approval/draft?formKey=LEAVE&data=' + encodeURIComponent(JSON.stringify({
              leaveType: '연차',
              startDate: first.startDate,
              endDate: first.endDate,
              days: first.days,
            })))
          }
        >
          직접 작성
        </button>
      </span>
      {apply.error && (
        <span style={{ display: 'block', fontSize: '12px', color: 'var(--danger)', marginTop: '6px' }}>
          {apply.error instanceof Error ? apply.error.message : '연차 신청을 올리지 못했습니다.'}
        </span>
      )}
    </Notice>
    <SubmittedPlans plans={submitted} />
    </>
  );
}

/**
 * 올렸고 <b>결재가 도는 중</b>인 계획 (사용자 지시 2026-08-26).
 *
 * <p>재촉하지 않는다 — 여기 선 줄에 사람이 할 일은 없고, 남은 것은 결재자의 시간이다. 그래서 색이
 * 다르고(정보) 버튼도 「보기」 하나다. 반려·회수되면 이 줄은 사라지고 위의 「올려 드릴까요」 로
 * 돌아온다 — 판정은 화면이 아니라 서버가 결재 상태로 한다.
 */
function SubmittedPlans({ plans }: { plans: UpcomingLeavePlan[] }) {
  const navigate = useNavigate();
  if (plans.length === 0) return null;

  const first = plans[0]!;

  return (
    <Notice tone="info">
      <b>결재 진행 중</b> — {dDayLabel(first.dDay)} 휴무 · {period(first.startDate, first.endDate)} ·{' '}
      {days(first.days)}일{plans.length > 1 && ` (그 밖에 ${plans.length - 1}건)`}
      <span style={{ display: 'block', fontSize: '12px', marginTop: '2px' }}>
        연차 신청을 올렸습니다. 승인이 끝나면 잔여에서 빠집니다.
      </span>
      {first.documentId && (
        <button
          className="btn-secondary"
          style={{ marginTop: '8px' }}
          onClick={() => navigate(`/approval?box=submitted&id=${first.documentId}`)}
        >
          결재 진행 보기
        </button>
      )}
    </Notice>
  );
}

/** `D-3` · `오늘` · `내일`. 숫자만 적으면 오늘인지 내일인지 매번 세어 봐야 한다. */
function dDayLabel(dDay: number): string {
  if (dDay <= 0) return '오늘';
  if (dDay === 1) return '내일';
  return `${dDay}일 뒤`;
}

// ── 요약 ─────────────────────────────────────────────────────────────────────

/**
 * 요약 — <b>올해 연차 · 사용 · 잔여는 회계 기준</b>(연차관리 표와 같은 숫자 · DOC-113), 소멸까지·의무사용은
 * 입사일 연차연도({@code period}) 값이다(사용자 결정 2026-10-07 · DOC-113 §6 (나) A).
 *
 * <p>서버가 회계 값을 보내기 전 판(재시작 전)에는 예전처럼 입사일 연차연도의 숫자로 물러선다.
 */
function Summary({
  period,
  fiscal,
  standard,
}: {
  period: LeavePeriod | null;
  fiscal: LeaveFiscal | null;
  standard: MyLeave['standard'];
}) {
  /*
    회계 값이 있으면 그것만 쓴다 — 입사일을 몰라 계·잔여가 비어 있어도 입사일 연차연도 숫자로 메우지 않는다
    (연차관리 표가 그 칸을 비워 두므로, 메우면 두 화면의 숫자가 갈린다 · 2026-10-26). 옛 서버(fiscal 없음)만 물러선다.
  */
  const total = fiscal ? fiscal.total : period?.grantedDays ?? 0;
  const used = fiscal ? fiscal.usedDays : period?.usedDays ?? 0;
  const remaining = fiscal ? fiscal.remainingDays : period?.remainingDays ?? 0;
  const usedRatio = total != null && total > 0 ? (used / total) * 100 : 0;
  const mandatory = period?.mandatoryDays ?? 0;
  const shortfall = period?.mandatoryShortfallDays ?? 0;
  const allowance = period?.allowanceDays ?? 0;

  const hasMandatory = standard.mandatoryUseDays != null && period !== null;

  /*
    부여·사용·잔여·소멸을 <b>왼쪽으로 몰고</b> 의무사용을 오른쪽에 붙인다(사용자 지시 2026-08-26).
    가로로 넉넉히 펼친 네 칸은 한 줄어치 정보를 화면 한 폭으로 먹었고, 그 아래 의무사용 상자가
    또 한 폭을 먹었다. 둘은 같은 물음의 앞뒤라(얼마 남았나 / 얼마를 꼭 써야 하나) 나란히 두면
    눈이 한 번에 읽는다. 좁은 화면에서는 다시 위아래로 쌓인다.
  */
  return (
    <div className={hasMandatory ? 'grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 items-start' : ''}>
      {/* 오른쪽 의무사용과 같은 테두리를 두른다 — 한쪽만 상자면 나란히 선 두 칸이 다른 무게로 읽힌다. */}
      <div
        className="space-y-3 rounded-lg h-full"
        style={{ border: '1px solid var(--border)', padding: '10px 12px' }}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label={fiscal ? `${fiscal.year}년 연차` : '부여'} value={total == null ? '-' : `${days(total)}일`} />
          <Stat label="사용" value={`${days(used)}일`} />
          <Stat label="잔여" value={remaining == null ? '-' : `${days(remaining)}일`} tone="accent" />
          {period && (
            <Stat
              label="소멸까지"
              value={period.expired ? '소멸됨' : `${period.daysUntilExpiry}일`}
              tone={!period.expired && period.daysUntilExpiry <= 60 ? 'warn' : undefined}
            />
          )}
        </div>

        <div className="rounded-full overflow-hidden" style={{ height: '6px', background: 'var(--bg-hover)' }}>
          <div style={{ width: `${Math.min(100, usedRatio)}%`, height: '100%', background: 'var(--accent)' }} />
        </div>

        <p style={mutedText}>
          {fiscal ? `${fiscal.year}-01-01 ~ ${fiscal.year}-12-31` : period ? `${period.startDate} ~ ${period.endDate}` : ''}
          {fiscal && fiscal.priorYearUsed > 0 && ` · 전년도 사용분 ${days(fiscal.priorYearUsed)}일 차감`}
          {period && ` · ${period.expiresOn} 소멸`}
          {period?.assumption && ' · 출근율 미확인 부여 포함'}
        </p>
      </div>

      {/*
        의무사용과 수당은 한 상자에 둔다. 둘은 같은 규칙의 앞뒤다 — 12일을 채우기 전의 잔여는
        수당이 아니고, 채운 뒤의 잔여가 수당이 된다. 떨어뜨려 놓으면 두 숫자가 서로 상관없어 보인다.
      */}
      {hasMandatory && (
        <div className="rounded-lg h-full" style={{ border: '1px solid var(--border)', padding: '10px 12px' }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
              사내 의무사용 {days(mandatory)}일
            </span>
          </div>

          <p style={{ ...mutedText, marginTop: '4px' }}>
            {shortfall > 0
              ? `${days(period?.usedDays ?? 0)}일 사용 — ${days(shortfall)}일을 더 써야 합니다.`
              : '의무사용 일수를 채웠습니다.'}
          </p>

          {standard.allowanceOverMandatory && (
            <p style={{ ...mutedText, marginTop: '2px' }}>
              수당 산정 대상 <b style={{ color: 'var(--text-primary)' }}>{days(allowance)}일</b>
              {' — 1일 통상임금 × 미사용 일수로 계산합니다. '}
              {/* <span>지급 금액과 반영 시점은 인사·급여 담당이 확정합니다.</span> */}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── 사용 동의서 ──────────────────────────────────────────────────────────────

/**
 * 연차 사용 동의서 (V420 · 사용자 결정 2026-10-07). 인사총무팀이 연차관리 표에서 요청하면 여기 뜬다.
 *
 * <p>서명은 결재에 등록해 둔 서명을 쓴다 — 서버가 그 그림을 동의서에 사본으로 굳힌다. 등록한 서명이
 * 없으면 같은 자리에서 서명 패드를 먼저 연다. 서명한 동의서는 더 띄우지 않는다(내가 할 일만 뜬다).
 */
function ConsentNotice() {
  const consents = useMyLeaveConsents();
  const signature = useMySignature();
  const sign = useSignLeaveConsent();
  const [padOpen, setPadOpen] = useState(false);

  const pending = (consents.data ?? []).filter((item) => !item.signedAt);
  if (pending.length === 0) return null;

  const hasSignature = Boolean(signature.data?.image);

  return (
    <>
      {pending.map((item) => (
        <Notice key={item.id} tone="warning">
          <b>{item.title ?? `${item.year}년 연차 사용 동의서`}</b>
          {item.body && (
            <span style={{ display: 'block', fontSize: '12px', marginTop: '4px', whiteSpace: 'pre-wrap' }}>{item.body}</span>
          )}
          <span className="flex items-center gap-2" style={{ marginTop: '8px' }}>
            {hasSignature && signature.data?.image && (
              <img src={signature.data.image} alt="내 서명" style={{ height: '36px', background: 'white', border: '1px solid var(--border)', borderRadius: '4px' }} />
            )}
            <button
              className="btn-primary"
              disabled={sign.isPending}
              onClick={() => (hasSignature ? sign.mutate(item.id) : setPadOpen(true))}
            >
              {hasSignature ? '서명' : '서명 등록'}
            </button>
            {hasSignature && (
              <button className="btn-secondary" onClick={() => setPadOpen(true)}>서명 바꾸기</button>
            )}
          </span>
          {sign.isError && <span style={{ display: 'block', fontSize: '12px', marginTop: '4px' }}>{sign.error.message}</span>}
        </Notice>
      ))}
      {padOpen && <SignaturePadModal onClose={() => setPadOpen(false)} />}
    </>
  );
}

// ── 촉진 ─────────────────────────────────────────────────────────────────────

/**
 * 사용촉진 안내. **답하지 않은 1차 촉구**가 있으면 맨 위에 뜬다 — 이 탭을 여는 이유가 그것이기
 * 때문이고, 답하지 않으면 회사가 사용 시기를 지정해 통보한다.
 */
function PromotionNotice({ promotions, onRespond }: { promotions: LeavePromotion[]; onRespond: () => void }) {
  const pending = promotions.find((item) => item.stage === 'FIRST' && !item.responded);
  const assigned = promotions.find((item) => item.stage === 'SECOND');

  if (assigned) {
    return (
      <Notice tone="warning">
        <b>사용 시기가 지정되었습니다</b> — {assigned.notifiedOn} 통보 · 당시 미사용 {days(assigned.remainingDays)}일
      </Notice>
    );
  }
  if (!pending) return null;

  return (
    <Notice tone="warning">
      <b>연차 사용촉진 안내</b> — {pending.notifiedOn} 기준 미사용 {days(pending.remainingDays)}일
      <span style={{ display: 'block', fontSize: '12px', marginTop: '2px' }}>
        사용 시기를 계획서로 알려 주세요. 회신이 없으면 회사가 사용 시기를 지정해 통보합니다.
      </span>
      <button className="btn-primary" style={{ marginTop: '8px' }} onClick={onRespond}>
        사용계획서 제출
      </button>
    </Notice>
  );
}

// ── 연 캘린더 ────────────────────────────────────────────────────────────────

const MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);
const DAYS_IN_MONTH = Array.from({ length: 31 }, (_, index) => index + 1);

/**
 * 12개월 × 31칸 격자. **계획은 테두리, 실적은 채움**으로 겹쳐 그린다.
 *
 * 두 축을 따로 그리면(계획 표 하나 · 실적 표 하나) "계획대로 썼는가" 를 눈으로 맞춰 봐야 한다.
 * 그것이 촉진에서 유일하게 중요한 물음이라 한 격자에 겹친다.
 */
function YearCalendar({
  year,
  onYear,
  data,
  loading,
}: {
  year: number;
  onYear: (next: number) => void;
  data: LeaveCalendar | undefined;
  loading: boolean;
}) {
  const planned = new Set((data?.plans ?? []).map((day) => day.date));
  const usedByDate = new Map((data?.usages ?? []).filter((day) => !day.cancelled).map((day) => [day.date, day]));
  // 연차 취소 신청으로 빠진 날(V544) — 회색 취소선으로 남긴다.
  const cancelledDates = new Set((data?.usages ?? []).filter((day) => day.cancelled).map((day) => day.date));

  return (
    // 카드 안에 들어가므로 위 구분선을 두지 않는다 — 카드 테두리가 이미 경계다.
    <div>
      <div className="flex items-center gap-2" style={{ marginBottom: '8px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>연 캘린더</h3>
        <button style={arrowButton} onClick={() => onYear(year - 1)} aria-label="이전 해">‹</button>
        <span style={{ fontSize: '13px', fontWeight: 700 }}>{year}</span>
        <button style={arrowButton} onClick={() => onYear(year + 1)} aria-label="다음 해">›</button>
        <span className="ml-auto flex items-center gap-3" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
          <span className="flex items-center gap-1">
            <span style={{ ...cellBox, borderColor: 'var(--accent)', display: 'inline-block' }} /> 계획
          </span>
          <span className="flex items-center gap-1">
            <span style={{ ...cellBox, background: 'var(--accent)', display: 'inline-block' }} /> 실적
          </span>
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><div className="spinner w-6 h-6" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table style={{ borderCollapse: 'collapse' }}>
            <tbody>
              {MONTHS.map((month) => (
                <tr key={month}>
                  <th
                    style={{
                      fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500,
                      textAlign: 'right', paddingRight: '6px', whiteSpace: 'nowrap',
                    }}
                  >
                    {month}월
                  </th>
                  {DAYS_IN_MONTH.map((day) => {
                    const date = `${year}-${pad(month)}-${pad(day)}`;
                    const exists = day <= daysInMonth(year, month);
                    const used = usedByDate.get(date);
                    const cancelled = !used && cancelledDates.has(date);
                    const isPlanned = planned.has(date);
                    return (
                      <td key={day} style={{ padding: '1px' }}>
                        <span
                          title={exists ? `${date}${used ? ` · ${used.leaveType ?? '사용'}` : cancelled ? ' · 취소' : isPlanned ? ' · 계획' : ''}` : undefined}
                          style={{
                            ...cellBox,
                            display: 'block',
                            visibility: exists ? 'visible' : 'hidden',
                            background: used
                              ? 'var(--accent)'
                              : cancelled
                                ? 'linear-gradient(to top right, #e5e7eb calc(50% - 1px), #6b7280 50%, #e5e7eb calc(50% + 1px))'
                                : 'transparent',
                            borderColor: isPlanned ? 'var(--accent)' : 'var(--border)',
                            // 계획 없이 쓴 날은 점선 — 계획서와 어긋난 날이 눈에 띄어야 한다.
                            borderStyle: used && !isPlanned ? 'dashed' : 'solid',
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── 목록 ─────────────────────────────────────────────────────────────────────

/**
 * 계획한 날들. 접어 둔다 — 위의 달별 막대가 「얼마나 지켰나」 를 이미 말하고, 이 목록은
 <b>어느 날인가</b>를 확인할 때만 편다.
 */
function PlanList({ plans }: { plans: PlanDay[] }) {
  const [open, setOpen] = useState(false);
  const ahead = plans.filter((plan) => !plan.fulfilled && plan.date >= todayIso()).length;

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
      <button
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}
      >
        계획한 날 {plans.length}일{ahead > 0 && ` · 앞으로 ${ahead}일`} {open ? '접기' : '보기'}
      </button>
      {open && (
        <div style={{ marginTop: '6px' }}>
          <PlanDateList plans={plans} today={todayIso()} />
        </div>
      )}
    </div>
  );
}

function UsageList({ usages }: { usages: LeaveUsage[] }) {
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
      <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>
        사용 내역 — 내가 쓴 연차
      </p>
      {usages.length === 0 ? (
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '12px 0' }}>
          이번 연차연도에 사용한 휴가가 없습니다.
        </p>
      ) : (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          {usages.map((usage, index) => (
            <div
              key={`${usage.startDate}-${index}`}
              className="flex items-baseline gap-3"
              style={{ padding: '7px 0', borderBottom: '1px solid var(--border)' }}
            >
              <span style={{ fontSize: '13px', color: 'var(--text-primary)', flexShrink: 0 }}>
                {period(usage.startDate, usage.endDate)}
              </span>
              <span className="badge badge-gray" style={{ flexShrink: 0 }}>{usage.leaveType}</span>
              {usage.cancel && <span className="badge badge-gray" style={{ flexShrink: 0 }}>취소</span>}
              {/* 잔여를 깎지 않는 휴가(경조·병가)를 구분한다 — 안 그러면 잔여가 틀린 줄 안다. */}
              {!usage.deducted && <span className="badge badge-blue" style={{ flexShrink: 0 }}>연차 차감 없음</span>}
              {usage.reason && (
                <span className="truncate" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {usage.reason}
                </span>
              )}
              <span
                className="ml-auto flex-shrink-0"
                style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}
              >
                {days(usage.days)}일
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** 지난 연차연도. 접어 둔다 — 여기 오는 이유의 대부분은 올해다. */
function PeriodHistory({ periods }: { periods: LeavePeriod[] }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
      <button
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}
      >
        지난 연차연도 {periods.length - 1}건 {open ? '접기' : '보기'}
      </button>
      {open && (
        <div style={{ marginTop: '6px' }}>
          {periods.slice(1).map((item) => (
            <div
              key={item.startDate}
              className="flex items-baseline gap-3"
              style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '12px' }}
            >
              <span style={{ color: 'var(--text-muted)', minWidth: '160px' }}>
                {item.startDate} ~ {item.endDate}
              </span>
              <span>부여 {days(item.grantedDays)} · 사용 {days(item.usedDays)}</span>
              {/* 소멸한 기간의 잔여는 "남은 것" 이 아니라 "날린 것" 이다. 말을 바꿔 적는다. */}
              <span className="ml-auto" style={{ fontWeight: 700 }}>
                {item.expired ? `소멸 ${days(item.remainingDays)}일` : `잔여 ${days(item.remainingDays)}일`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 거들기 ───────────────────────────────────────────────────────────────────

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'accent' | 'warn' }) {
  const color = tone === 'accent' ? 'var(--accent)' : tone === 'warn' ? 'var(--danger)' : 'var(--text-primary)';
  return (
    <div className="rounded-lg px-3 py-2.5 text-center" style={{ background: 'var(--bg-hover)' }}>
      <p className="font-bold" style={{ fontSize: '18px', color }}>{value}</p>
      <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{label}</p>
    </div>
  );
}

function days(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** `08-05` · `08-05 ~ 08-07`. 연도는 위의 기간 표기가 이미 말하고 있다. */
function period(startDate: string, endDate: string): string {
  const from = startDate.slice(5);
  return startDate === endDate ? from : `${from} ~ ${endDate.slice(5)}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

const cellBox = {
  width: '10px',
  height: '10px',
  borderRadius: '2px',
  border: '1px solid var(--border)',
} as const;

const arrowButton = { fontSize: '14px', color: 'var(--text-secondary)', padding: '0 4px' } as const;

const mutedText = { fontSize: '12px', color: 'var(--text-muted)' } as const;
